import classNames from 'classnames';
import { Fragment, useCallback } from 'react';
import { Button, Heading, List } from 'yoso';
import { useLogin } from '~/components/Auth';
import { BaseCard } from '~/components/Card';
import {
  useChallengeBoostCriteria,
  useChallengeRewardCriteria,
} from '~/components/Challenge/Boost/ChallengeBoost.hooks';
import { useChallengeEvent } from '~/components/Challenge/Challenge.hooks';
import cardStyles from '~/components/Challenge/Guide/ChallengeGuideCard.module.scss';
import {
  useChallengeScore,
  useProceedScoreUp,
  useScoreUpCriteria,
} from '~/components/Challenge/Score/ChallengeScore.hooks';
import { useEmbeddedViewer } from '~/components/Embedded/Embedded.hooks';
import { T, t } from '~/components/I18n';
import { BoostBox } from '~/components/Icon/BoostBox';
import crownCitizen from '~/components/Icon/Citizen/crownCitizen.png';
import { ShiningBasket } from '~/components/Icon/ShiningBasket';
import confettiCat from '~/components/Icon/confettiCat.png';
import gemChest from '~/components/Icon/gemChest.png';
import kaiaReward from '~/components/Icon/kaiaReward.png';
import { DescriptionList } from '~/components/List/Description/DescriptionList';
import { useMember } from '~/components/Member/Member.hooks';
import { format, formatBoost } from '~/components/Util/number';
import { useWalletConnection } from '~/components/Wallet/Wallet.hooks';
import styles from './ChallengeGuide.module.scss';

export type ChallengeGuideProps = {
  className?: string;
};

export const ChallengeGuide = ({ className }: ChallengeGuideProps) => {
  const {
    data: { region = 'GL', isLoggedIn },
  } = useMember();

  const {
    data: { boostDetailList, maxBoostPerLevelCount, maxBoost, maxPetBoost },
  } = useChallengeBoostCriteria();
  const citizenDetailList = boostDetailList?.filter(({ itemType }) => itemType === 'CITIZEN');

  const {
    data: { registered, sellerInfoUrl },
  } = useWalletConnection(undefined, { enabled: isLoggedIn });

  const {
    data: { score },
  } = useChallengeScore({ enabled: isLoggedIn });
  const { mutateAsync, isLoading } = useProceedScoreUp();
  const {
    data: { baseScore, joinForAssetAmount },
  } = useScoreUpCriteria({ useErrorBoundary: false });
  const {
    data: { maxPurchaseRewardRate },
  } = useChallengeRewardCriteria();

  const {
    data: { challengeStatus },
  } = useChallengeEvent();

  const { open: openWallet } = useEmbeddedViewer();
  const login = useLogin();
  // TODO: replace with AuthButton
  const onWalletClick = useCallback(() => {
    if (!isLoggedIn) {
      login();
      return;
    }
    if (!registered) {
      openWallet(sellerInfoUrl);
    }
  }, [isLoggedIn, login, openWallet, registered, sellerInfoUrl]);

  const onScoreUpClick = useCallback(() => {
    mutateAsync();
  }, [mutateAsync]);

  // NOTE: in inquiry
  const isScoreUpDisabled = !isLoggedIn || !score || isLoading || challengeStatus === 'CLOSED';

  return (
    <div className={classNames(styles.root, className)}>
      <div className={styles.header}>
        <Heading level={3} className={styles.title}>
          <T name="citizen_howto_title" />
        </Heading>
      </div>
      <List className={styles.body}>
        <li>
          <BaseCard className={cardStyles.root} color="muted" size="large">
            <ShiningBasket className={cardStyles.image} width={110} height={110} />
            <div className={cardStyles.body}>
              <Heading level={4} className={cardStyles.title}>
                <T name="citizen_howto_score_title" />
              </Heading>
              <div className={cardStyles.description}>
                <T name="citizen_howto_score_desc" />
                <p className={cardStyles.conversion}>
                  <T name="citizen_howto_score_don" />
                  <strong>
                    <T
                      name="citizen_howto_score_don_num"
                      className={cardStyles.conversionHighlight}
                      options={[
                        <svg
                          key={0}
                          width="4"
                          height="5"
                          viewBox="0 0 4 5"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M3.85037 2.22788L0.533766 0.0574582C0.306905 -0.0910021 -4.32045e-08 0.0654641 0 0.329583L1.44015e-07 4.67042C1.44015e-07 4.93454 0.306905 5.091 0.533766 4.94254L3.85037 2.77212C4.04988 2.64156 4.04988 2.35844 3.85037 2.22788Z"
                            fill="black"
                          />
                        </svg>,
                      ]}
                    />
                  </strong>
                </p>
              </div>
              <p className={cardStyles.disclaimer}>
                <T name="citizen_howto_score_caution1" />
              </p>
              <p className={cardStyles.disclaimer}>
                <T name="citizen_howto_score_caution2" />
              </p>
            </div>
          </BaseCard>
        </li>
        <li>
          <BaseCard className={cardStyles.root} color="muted" size="large">
            <BoostBox className={cardStyles.image} width={110} height={110} />
            <div className={cardStyles.body}>
              <Heading level={4} className={cardStyles.title}>
                <T name="citizen_howto_boost_title" />
              </Heading>
              <p className={cardStyles.description}>
                <T name="citizen_howto_boost_desc" />
              </p>
              <List className={cardStyles.list}>
                <li>
                  <div className={cardStyles.boost}>
                    <div className={cardStyles.boostHeader}>
                      <img
                        src={crownCitizen}
                        width={35}
                        className={cardStyles.boostImageCitizen}
                        alt="Membership boost"
                      />
                    </div>
                    <div className={cardStyles.boostBody}>
                      <Heading level={5} className={cardStyles.boostTitle}>
                        Citizen
                      </Heading>
                      <DescriptionList layout="inline" className={cardStyles.boostDescription}>
                        {citizenDetailList.map(({ level, boost }) => (
                          <Fragment key={level}>
                            <dt>Lv{level}</dt>
                            <dd>
                              <strong>
                                {formatBoost(boost, { decimalPlacesPadding: false })}%
                              </strong>
                            </dd>
                          </Fragment>
                        ))}
                      </DescriptionList>
                      <p className={cardStyles.boostSubDescription}>
                        <T
                          name="citizen_howto_boost_citizen_max"
                          options={[format(maxBoostPerLevelCount), formatBoost(maxBoost)]}
                        />
                      </p>
                    </div>
                  </div>
                </li>
                <li>
                  <div className={cardStyles.boost}>
                    <div className={cardStyles.boostHeader}>
                      <img
                        src={confettiCat}
                        className={cardStyles.boostImageFriend}
                        width={42}
                        alt="confetti cat"
                      />
                    </div>
                    <div className={cardStyles.boostBody}>
                      <Heading level={5} className={cardStyles.boostTitle}>
                        Friends
                      </Heading>
                      <p className={cardStyles.boostDescription}>
                        Boost{' '}
                        <strong>
                          <T name="citizen_howto_boost_friend_max" />
                        </strong>
                      </p>
                      <p className={cardStyles.boostSubDescription}>
                        <T
                          name="citizen_howto_boost_friend_max_desc"
                          options={[formatBoost(maxPetBoost)]}
                        />
                      </p>
                    </div>
                  </div>
                </li>
              </List>
            </div>
          </BaseCard>
        </li>
        <li>
          <BaseCard className={cardStyles.root} color="muted" size="large">
            <img
              src={gemChest}
              alt={t('citizen_howto_scoreup_title')}
              width={110}
              height={110}
              className={cardStyles.image}
            />
            <div className={cardStyles.body}>
              <Heading level={4} className={cardStyles.title}>
                <T name="citizen_howto_scoreup_title" />
              </Heading>
              <p className={cardStyles.description}>
                <T
                  name="citizen_howto_scoreup_desc"
                  options={[format(baseScore), format(joinForAssetAmount)]}
                />
              </p>
              <Button
                color="primary"
                className={cardStyles.button}
                disabled={isScoreUpDisabled}
                onClick={onScoreUpClick}
                data-event="click"
                data-event-target="scoreup_btn_in_challengeguide">
                <T name="citizen_howto_scoreup_btn" />
              </Button>
            </div>
          </BaseCard>
        </li>
        <li>
          <BaseCard className={cardStyles.root} color="muted" size="large">
            <img
              src={kaiaReward}
              className={cardStyles.image}
              width={110}
              height={110}
              alt="kaia reward"
            />
            <div className={cardStyles.body}>
              <Heading level={4} className={cardStyles.title}>
                <T name="citizen_howto_reward_title" />
              </Heading>
              <p className={cardStyles.description}>
                <T
                  name={
                    region === 'JP'
                      ? 'citizen_howto_reward_desc_jp'
                      : 'citizen_howto_reward_desc_gl'
                  }
                  options={[maxPurchaseRewardRate]}
                />
              </p>
              <Button
                color="primary"
                className={cardStyles.button}
                disabled={registered}
                onClick={onWalletClick}
                data-event="click"
                data-event-target="connectwallet_btn_in_challengeguide">
                <T
                  name={
                    registered
                      ? 'citizen_howto_reward_button_wallet_after'
                      : 'citizen_howto_reward_button_wallet_before'
                  }
                />
              </Button>
            </div>
          </BaseCard>
        </li>
      </List>
    </div>
  );
};
